/**
 * Onboarding integration promo card — matches Step 7 Twilio styling
 * with inline vault-backed configure drawer (Integrations Console parity).
 */

import { useQuery } from "@tanstack/react-query";
import type { LucideIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { IntegrationConfigureDrawer } from "../integrations/IntegrationConfigureDrawer";
import { api } from "../../lib/api";

export function OnboardingIntegrationCard({
  integrationType,
  titleKey,
  bodyKey,
  icon: Icon,
  needsLegalEntityHintKey = "app.onb_integration_needs_le",
}: {
  integrationType: string;
  titleKey: string;
  bodyKey: string;
  icon: LucideIcon;
  /** i18n key shown when no legal entity exists yet (Step 1 welcome). */
  needsLegalEntityHintKey?: string;
}) {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data: entities, isLoading: entitiesLoading } = useQuery({
    queryKey: ["legal-entities", false, ""],
    queryFn: () => api.listLegalEntities({}),
  });
  const legalEntityId = entities?.[0]?.id ?? null;

  const { data: connections, isLoading: connectionsLoading } = useQuery({
    queryKey: ["connections", legalEntityId],
    queryFn: () => api.listConnections(legalEntityId!),
    enabled: legalEntityId != null,
  });

  const conn = connections?.connections.find(
    (c) => c.integration_type === integrationType,
  );
  const configured = conn?.configured ?? false;
  const loading = entitiesLoading || (legalEntityId != null && connectionsLoading);
  const canConfigure = legalEntityId != null && conn != null;

  return (
    <>
      <section className="rounded-xl border border-ai-subtle bg-ai-bg p-5">
        <header className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-surface-default text-ai">
            <Icon size={18} aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-semibold text-text-primary">
                {t(titleKey)}
              </h2>
              {!loading && legalEntityId != null && (
                <span
                  className={[
                    "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                    configured
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-amber-100 text-amber-800",
                  ].join(" ")}
                >
                  {configured
                    ? t("app.int_status_connected")
                    : t("app.int_status_not_connected")}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-text-secondary">{t(bodyKey)}</p>
            {!legalEntityId && !entitiesLoading && (
              <p className="mt-2 text-xs text-text-tertiary">
                {t(needsLegalEntityHintKey)}{" "}
                <Link
                  to="/onboarding/company"
                  className="text-brand hover:underline"
                >
                  {t("app.onb_step_company")}
                </Link>
                .
              </p>
            )}
          </div>
          <button
            type="button"
            disabled={!canConfigure}
            onClick={() => setDrawerOpen(true)}
            className="shrink-0 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {configured ? t("app.int_edit") : t("app.s7_connect_btn")}
          </button>
        </header>
      </section>

      {canConfigure && conn && legalEntityId && (
        <IntegrationConfigureDrawer
          conn={conn}
          legalEntityId={legalEntityId}
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
        />
      )}
    </>
  );
}
